import { useMemo, useState, type ReactNode } from 'react';
import type { DynamicFormDefinition, FormColumn, FormField, FormRow } from '../../models/form';
import type { DesignerSelection } from './designer-utils';
import { isMultipleFileField } from '../../utils/file-values';

interface Props {
  form: DynamicFormDefinition;
  activeTab: string;
  selection?: DesignerSelection;
  onChangeTab: (tabName: string) => void;
  onSelect: (selection: DesignerSelection) => void;
}

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase('fa-IR');
}

function fieldLabel(field: FormField): string {
  return field.designerTitle?.trim() || field.title?.trim() || field.name || field.type;
}

function fieldTypeLabel(field: FormField): string {
  if (field.type === 'input' && field.calculationFormula) return 'محاسباتی';
  if (field.type === 'select' && field.lookupEnabled) return 'جست‌وجویی';
  if (field.type === 'fileupload' && isMultipleFileField(field)) return 'چندفایلی';
  return field.type;
}

function matchesField(field: FormField, needle: string): boolean {
  if (!needle) return true;
  return normalized(`${fieldLabel(field)} ${field.name} ${field.type} ${fieldTypeLabel(field)}`).includes(needle);
}

function fieldContainsMatch(field: FormField, needle: string): boolean {
  if (matchesField(field, needle)) return true;
  if (field.children?.some((child) => fieldContainsMatch(child, needle))) return true;
  if (field.tableColumns?.some((child) => fieldContainsMatch(child, needle))) return true;
  if (field.panelRows?.some((row) => rowContainsMatch(row, needle))) return true;
  if (field.tableRows?.some((row) => rowContainsMatch(row, needle))) return true;
  return false;
}

function columnContainsMatch(column: FormColumn, needle: string): boolean {
  return column.fields.some((field) => fieldContainsMatch(field, needle))
    || Boolean(column.rows?.some((row) => rowContainsMatch(row, needle)));
}

function rowContainsMatch(row: FormRow, needle: string): boolean {
  return row.columns.some((column) => columnContainsMatch(column, needle));
}

export function LayersTree({ form, activeTab, selection, onChangeTab, onSelect }: Props) {
  const [term, setTerm] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const needle = normalized(term);

  const visibleTabs = useMemo(() => form.Tabs.filter((tab) => !needle || normalized(`${tab.title} ${tab.name}`).includes(needle) || tab.rows.some((row) => rowContainsMatch(row, needle))), [form, needle]);

  const toggle = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const reveal = (kind: 'field' | 'layout', id: string) => {
    window.requestAnimationFrame(() => {
      const selector = kind === 'field' ? `[data-field-id="${CSS.escape(id)}"]` : `[data-layout-id="${CSS.escape(id)}"]`;
      document.querySelector<HTMLElement>(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  };

  const selectField = (tabName: string, fieldId: string) => {
    if (activeTab !== tabName) onChangeTab(tabName);
    onSelect({ kind: 'field', id: fieldId });
    reveal('field', fieldId);
  };

  const selectLayout = (tabName: string, rowId: string) => {
    if (activeTab !== tabName) onChangeTab(tabName);
    onSelect({ kind: 'layout', id: rowId });
    reveal('layout', rowId);
  };

  const renderFields = (fields: FormField[], tabName: string, depth: number): ReactNode => fields
    .filter((field) => !needle || fieldContainsMatch(field, needle))
    .map((field) => {
      const hasChildren = Boolean(field.children?.length || field.tableColumns?.length || field.panelRows?.length || field.tableRows?.length);
      const key = `field:${field.id}`;
      const isCollapsed = collapsed.has(key) && !needle;
      return <li key={field.id} className={`df-layer-node df-layer-field ${selection?.kind === 'field' && selection.id === field.id ? 'is-selected' : ''}`}>
        <div className="df-layer-node-row" style={{ paddingInlineStart: `${Math.min(depth, 8) * 12}px` }}>
          {hasChildren ? <button type="button" className="df-layer-expander" aria-label={isCollapsed ? 'بازکردن' : 'بستن'} onClick={() => toggle(key)}>{isCollapsed ? '▸' : '▾'}</button> : <span className="df-layer-expander-placeholder" />}
          <button type="button" className="df-layer-select" onClick={() => selectField(tabName, field.id)} title={`${field.title || field.name} (${field.name})`}>
            <span className="df-layer-icon" aria-hidden="true">◇</span>
            <span className="df-layer-title">{fieldLabel(field)}</span>
            <small>{fieldTypeLabel(field)}</small>
          </button>
        </div>
        {hasChildren && !isCollapsed ? <ul>
          {field.children?.length ? <li className="df-layer-group"><span>محتوای پنل</span><ul>{renderFields(field.children, tabName, depth + 1)}</ul></li> : null}
          {field.panelRows?.length ? <li className="df-layer-group"><span>لایه‌های پنل</span><ul>{renderRows(field.panelRows, tabName, depth + 1)}</ul></li> : null}
          {field.tableColumns?.length ? <li className="df-layer-group"><span>ستون‌های جدول</span><ul>{renderFields(field.tableColumns, tabName, depth + 1)}</ul></li> : null}
          {field.tableRows?.length ? <li className="df-layer-group"><span>ورودی جدول</span><ul>{renderRows(field.tableRows, tabName, depth + 1)}</ul></li> : null}
        </ul> : null}
      </li>;
    });

  const renderRows = (rows: FormRow[], tabName: string, depth: number): ReactNode => rows
    .filter((row) => !needle || rowContainsMatch(row, needle))
    .map((row, rowIndex) => {
      const key = `row:${row.id}`;
      const isCollapsed = collapsed.has(key) && !needle;
      return <li key={row.id} className={`df-layer-node df-layer-layout ${selection?.kind === 'layout' && selection.id === row.id ? 'is-selected' : ''}`}>
        <div className="df-layer-node-row" style={{ paddingInlineStart: `${Math.min(depth, 8) * 12}px` }}>
          <button type="button" className="df-layer-expander" aria-label={isCollapsed ? 'بازکردن' : 'بستن'} onClick={() => toggle(key)}>{isCollapsed ? '▸' : '▾'}</button>
          <button type="button" className="df-layer-select" onClick={() => selectLayout(tabName, row.id)} title={`لایه ${rowIndex + 1}`}>
            <span className="df-layer-icon" aria-hidden="true">▤</span>
            <span className="df-layer-title">لایه {rowIndex + 1}</span>
            <small>{row.columns.length} ستون</small>
          </button>
        </div>
        {!isCollapsed ? <ul>{row.columns.map((column, columnIndex) => {
          if (needle && !columnContainsMatch(column, needle)) return null;
          const columnKey = `column:${column.id}`;
          const columnCollapsed = collapsed.has(columnKey) && !needle;
          return <li key={column.id} className="df-layer-node df-layer-column">
            <div className="df-layer-node-row" style={{ paddingInlineStart: `${Math.min(depth + 1, 8) * 12}px` }}>
              <button type="button" className="df-layer-expander" aria-label={columnCollapsed ? 'بازکردن' : 'بستن'} onClick={() => toggle(columnKey)}>{columnCollapsed ? '▸' : '▾'}</button>
              <span className="df-layer-column-label"><span aria-hidden="true">▥</span> ستون {columnIndex + 1}<small>{column.span}/12</small></span>
            </div>
            {!columnCollapsed ? <ul>
              {renderFields(column.fields, tabName, depth + 2)}
              {column.rows?.length ? <li className="df-layer-group"><span>لایه‌های تو در تو</span><ul>{renderRows(column.rows, tabName, depth + 2)}</ul></li> : null}
            </ul> : null}
          </li>;
        })}</ul> : null}
      </li>;
    });

  return <div className="df-layers-tree" aria-label="درخت لایه‌ها و کنترل‌ها">
    <div className="df-layer-search">
      <span aria-hidden="true">⌕</span>
      <input className="form-control input-sm" value={term} onChange={(event) => setTerm(event.target.value)} placeholder="جستجو در لایه‌ها…" aria-label="جستجو در لایه‌ها" />
      {term ? <button type="button" onClick={() => setTerm('')} aria-label="پاک‌کردن جستجو">×</button> : null}
    </div>
    <ul className="df-layer-root">{visibleTabs.map((tab) => {
      const key = `tab:${tab.name}`;
      const isCollapsed = collapsed.has(key) && !needle;
      return <li key={tab.name} className={`df-layer-tab ${activeTab === tab.name ? 'is-active' : ''}`}>
        <div className="df-layer-node-row">
          <button type="button" className="df-layer-expander" aria-label={isCollapsed ? 'بازکردن' : 'بستن'} onClick={() => toggle(key)}>{isCollapsed ? '▸' : '▾'}</button>
          <button type="button" className="df-layer-select" onClick={() => onChangeTab(tab.name)}>
            <span className="df-layer-icon" aria-hidden="true">▣</span>
            <span className="df-layer-title">{tab.title || tab.name}</span>
            <small>{tab.name}</small>
          </button>
        </div>
        {!isCollapsed ? <ul>{renderRows(tab.rows, tab.name, 1)}</ul> : null}
      </li>;
    })}</ul>
    {!visibleTabs.length ? <div className="df-empty-settings">موردی با این عبارت پیدا نشد.</div> : null}
  </div>;
}
